import React, { Component, ErrorInfo, useEffect } from 'react';
import { View, Text, Pressable, Platform, StyleSheet } from 'react-native';
import { HugeiconsIcon } from '@hugeicons/react-native';
import { DeadIcon } from '@hugeicons-pro/core-stroke-rounded';
import * as Sentry from '@sentry/react-native';
import i18n from '@/i18n/config';
import { useTranslation } from 'react-i18next';

let _triggerGlobalCrash: ((message: string) => void) | null = null;

export function triggerGlobalCrash(message: string) {
  _triggerGlobalCrash?.(message);
}

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  errorMessage: string | null;
}

export class AppErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, errorMessage: null };

  componentDidMount() {
    _triggerGlobalCrash = (message) => {
      this.setState({ hasError: true, errorMessage: message });
    };
  }

  componentWillUnmount() {
    _triggerGlobalCrash = null;
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, errorMessage: error?.message || null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    Sentry.captureException(error, {
      extra: { componentStack: errorInfo.componentStack },
    });
    console.error('[AppErrorBoundary]', error, errorInfo.componentStack);
  }

  private handleReload = () => {
    if (Platform.OS === 'web') {
      window.location.reload();
    } else {
      this.setState({ hasError: false });
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <View style={styles.container}>
          <View style={styles.content}>
            <HugeiconsIcon icon={DeadIcon} size={80} color="#555" />
            <Text style={styles.title}>{i18n.t('common.error.crashTitle')}</Text>
            <Text style={styles.subtitle}>{i18n.t('common.error.crashSubtitle')}</Text>
            <Pressable
              onPress={this.handleReload}
              style={({ pressed }) => [styles.button, pressed && styles.buttonPressed]}
            >
              <Text style={styles.buttonText}>{i18n.t('common.error.crashReload')}</Text>
            </Pressable>
          </View>
          {this.state.errorMessage ? (
            <Text style={styles.errorDetail} numberOfLines={3}>
              {this.state.errorMessage}
            </Text>
          ) : null}
        </View>
      );
    }

    return this.props.children;
  }
}

export function RouteErrorBoundary({ error, retry }: { error: Error; retry: () => void }) {
  const { t } = useTranslation();

  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  return (
    <View style={styles.container}>
      <View style={styles.content}>
        <HugeiconsIcon icon={DeadIcon} size={80} color="#555" />
        <Text style={styles.title}>{t('common.error.crashTitle')}</Text>
        <Text style={styles.subtitle}>{t('common.error.crashSubtitle')}</Text>
        <Pressable
          onPress={retry}
          style={({ pressed }) => [styles.button, pressed && styles.buttonPressed]}
        >
          <Text style={styles.buttonText}>{t('common.error.crashRetry')}</Text>
        </Pressable>
      </View>
      {error?.message ? (
        <Text style={styles.errorDetail} numberOfLines={3}>
          {error.message}
        </Text>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0e0e0e',
    padding: 32,
  },
  content: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 12,
  },
  title: {
    color: '#e5e5e5',
    fontSize: 20,
    fontWeight: '600',
    marginTop: 8,
  },
  subtitle: {
    color: '#777',
    fontSize: 15,
    textAlign: 'center',
    lineHeight: 22,
    maxWidth: 320,
  },
  button: {
    backgroundColor: '#252525',
    paddingHorizontal: 32,
    paddingVertical: 14,
    borderRadius: 12,
    marginTop: 12,
    borderWidth: 1,
    borderColor: '#333',
  },
  buttonPressed: {
    backgroundColor: '#333',
  },
  buttonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  errorDetail: {
    color: '#555',
    fontSize: 11,
    textAlign: 'center',
    maxWidth: 320,
    alignSelf: 'center',
    paddingBottom: 16,
  },
});
